import styled from '@emotion/styled'
import {
  Breakpoint,
  Box,
  Button,
  Display,
  FlexDirection,
  FormFieldStatus,
  Input,
} from '@gousto-internal/citrus-react'
import { applyPromoCode, matchResult } from '@library/api-user'
import { queueTrackingEvent } from '@library/tracking-integration'
import { Field, Form, FieldProps, useFormik, FormikProvider } from 'formik'
import React, { useState } from 'react'
import { PromoCodeInfoPanel } from '../PromoCodeInfoPanel/PromoCodeInfoPanel'

type TrackingPromoCodeSubmit = {
  action: string
  properties: {
    page_url: string
  }
}
const PromoFormRow = styled.div`
  display: flex;
  align-items: flex-end;
  margin-top: 16px;

  @media (max-width: ${Breakpoint.Small}) {
    flex-direction: column;
    align-items: stretch;

    button {
      margin-left: 0;
      margin-top: 8px;
      width: 100%;
    }
  }
`

const StyledButton = styled(Button)`
  margin-left: 8px;
  margin-bottom: 0;
  height: 48px;
  min-width: 148px;
`

interface IInfoPanelContents {
  title: string
  message: string
}

export const PromoCodeSection = () => {
  const [success, setSuccess] = useState<IInfoPanelContents>()
  const [error, setError] = useState<IInfoPanelContents>()
  const formik = useFormik({
    initialValues: {
      promocode: '',
    },
    onSubmit: async (value) => {
      queueTrackingEvent<TrackingPromoCodeSubmit>({
        action: 'promocode_enter',
        properties: {
          page_url: window.location.pathname,
        },
      })

      setError(undefined)
      setSuccess(undefined)
      setFormInputStatus(undefined)

      applyPromoCode(value.promocode)
        .then((result) =>
          matchResult(result, {
            ok: (result) => {
              queueTrackingEvent<TrackingPromoCodeSubmit>({
                action: 'promocode_success',
                properties: {
                  page_url: window.location.pathname,
                },
              })
              setSuccess({
                title: result.result.data,
                message: 'Your discount has been applied to your account. Happy cooking!',
              })
              setFormInputStatus(FormFieldStatus.Success)
            },
            err: (error) => {
              queueTrackingEvent<TrackingPromoCodeSubmit>({
                action: 'promocode_failed',
                properties: {
                  page_url: window.location.pathname,
                },
              })
              const details = error['error-details']?.formatted_message
              setError({
                title: details?.title || 'Something went wrong',
                message:
                  details?.message ||
                  "Double-check the code and try again, or head to our Help Centre if you're stuck.",
              })
              setFormInputStatus(FormFieldStatus.Error)
            },
          }),
        )
        .catch(() => {
          setError({
            title: 'Something went wrong',
            message:
              "Double-check the code and try again, or head to our Help Centre if you're stuck.",
          })
          setFormInputStatus(FormFieldStatus.Error)
        })
    },
  })

  const [formInputStatus, setFormInputStatus] = useState<FormFieldStatus | undefined>(undefined)

  return (
    <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={4}>
      <FormikProvider value={formik}>
        <Form>
          <PromoFormRow>
            <Box display={Display.Flex} flexDirection={FlexDirection.Column} flexGrow={1}>
              <Field
                name="promocode"
                type="text"
                placeholder="Enter your promo code"
                data-testid="promoCodeInput"
              >
                {({ field }: FieldProps) => (
                  <Input
                    id="promocode"
                    placeholder="Enter your promo code"
                    autoComplete="off"
                    status={formInputStatus}
                    {...field}
                  />
                )}
              </Field>
            </Box>
            <StyledButton type="submit" disabled={!formik.values.promocode}>
              Enter code
            </StyledButton>
          </PromoFormRow>
        </Form>
        {success && (
          <PromoCodeInfoPanel title={success.title} message={success.message} type="success" />
        )}
        {error && <PromoCodeInfoPanel title={error.title} message={error.message} type="error" />}
      </FormikProvider>
    </Box>
  )
}
